import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { ExpenseInputFilter } from "domain/models/expense-input-filter";
import { useDeleteExpenseMutation, useExpenseByFilter } from "presentation/hooks/use-query-expense";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import ExpenseSkeleton from "./Skeleton";
import { Expense } from "domain/entities/expense";
import { ExpenseStatusEnum } from "domain/enum/expenseStatus";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Document } from "domain/entities/document";
import RegisterExpensesModal from "./components/RegisterExpensesModal";
import MobileS3PDFViewerDialog from "presentation/mobile/components/MobileS3PDFViewerDialog";
import MobileImageViewerDialog from "presentation/mobile/components/MobileImageViewerDialog";
import usePermissionCheck from "presentation/hooks/use-check-permissions";
import useLongPress from "presentation/mobile/hooks/useLongPress";
import MobileFilterTabs from "presentation/mobile/components/MobileFilterTabs";
import { useStatusFilter } from "presentation/mobile/hooks/useStatusFilter";
import { EXPENSE_STATUS_FILTERS } from "presentation/mobile/utils/status-filters.util";
import MobileActionButton from "presentation/mobile/components/MobileActionButton";
import { formatCurrency, formatDate } from "presentation/mobile/utils/format.util";
import MobileToast from "presentation/mobile/components/MobileToast";
import MobileRefreshingIndicator from "presentation/mobile/components/MobileRefreshingIndicator";
import MobileRefreshingList from "presentation/mobile/components/MobileRefreshingList";
import { getApiErrorMessage } from "presentation/mobile/utils/api-error.util";
import { EXPENSE_STATUS_TONES, getStatusStyle } from "presentation/mobile/utils/status-styles.util";

const FUNCTION = "expenses";
const REQUIRE_FUNCTION = "expenses";

const getExpenseStatus = (expense: Expense) => expense.status;

interface ExpenseItemProps {
    expense: Expense;
    isExpanded: boolean;
    onToggle: () => void;
    onView: (expense: Expense) => void;
    onCancel: (expense: Expense) => void;
}

const ExpenseItem: React.FC<ExpenseItemProps> = ({
    expense,
    isExpanded,
    onToggle,
    onView,
    onCancel,
}) => {
    const { isPressing, handlers } = useLongPress({
        onLongPress: onToggle,
    });

    const statusInfo = {
        label: expense.status ?? "Pendente",
        ...getStatusStyle(EXPENSE_STATUS_TONES, expense.status),
    };

    const canCancel = expense.status === ExpenseStatusEnum.PENDENTE;

    return (
        <div
            {...handlers}
            className="border-round-xl p-3 flex flex-column gap-2 cursor-pointer"
            style={{
                backgroundColor: "#FFFFFF",
                border: isExpanded ? "1.5px solid #0084D1" : "1px solid #E2E8F0",
                boxShadow: isExpanded ? "0 4px 14px rgba(0, 132, 209, 0.08)" : "none",
                transform: isPressing ? "scale(0.99)" : "scale(1)",
                transition: "all 0.2s ease",
            }}
        >
            {/* Linha 1: Tipo de Despesa + Nome + Badge de Estado + Botão '+' / 'x' */}
            <div className="w-full flex justify-content-between align-items-center">
                <div className="flex align-items-center gap-1 overflow-hidden pr-2">
                    {isExpanded && (
                        <span
                            style={{
                                color: "#0084D1",
                                fontSize: "1.1rem",
                                lineHeight: "1",
                                marginRight: "2px",
                            }}
                        >

                        </span>
                    )}
                    <span className="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {`${expense.type} - ${expense.employee?.name ?? ""}`}
                    </span>
                </div>

                <div className="flex align-items-center gap-2 flex-shrink-0">
                    <span
                        className="badge-squircle"
                        style={{
                            color: statusInfo.color,
                            backgroundColor: statusInfo.bg,
                            border: `1px solid ${statusInfo.border}`,
                        }}
                    >
                        {statusInfo.label}
                    </span>

                    {/* Micro-interação: '+' gira 45º para 'x' */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                        className="border-none bg-transparent cursor-pointer flex align-items-center justify-content-center p-0"
                        style={{
                            width: "24px",
                            height: "24px",
                            color: "#94A3B8",
                            outline: "none",
                        }}
                        aria-label={isExpanded ? "Recolher" : "Expandir"}
                    >
                        <i
                            className="pi pi-plus text-xs"
                            style={{
                                transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Linha 2: Data da Despesa e Valor em Destaque */}
            <div className="w-full flex justify-content-between align-items-center">
                <span className="text-xs" style={{ color: "#64748B" }}>
                    Data: <strong style={{ color: "#0F172A" }}>{formatDate(expense.date ?? expense.submissionDate)}</strong>
                </span>
                <span className="font-bold text-base" style={{ color: "#0084D1" }}>
                    {formatCurrency(expense.value)}
                </span>
            </div>

            {/* Descrição curta (se houver) */}
            {expense.description && (
                <div className="w-full">
                    <span
                        className="text-xs text-600 block overflow-hidden text-overflow-ellipsis"
                        style={{ maxWidth: "100%", whiteSpace: "nowrap" }}
                    >
                        {expense.description}
                    </span>
                </div>
            )}

            {/* Secção Expandida: APENAS OS BOTÕES HORIZONTAIS COMPACTOS ALINHADOS À DIREITA */}
            {isExpanded && (
                <div
                    className="w-full flex flex-column gap-2 pt-2 mt-1"
                    style={{ borderTop: "1px solid #F1F5F9" }}
                >
                    {/* Motivo de Rejeição (se existir) */}
                    {expense.rejectionReason && (
                        <div
                            className="w-full p-2 border-round-md"
                            style={{ backgroundColor: "#FEF2F2", border: "1px solid #FEE2E2" }}
                        >
                            <span className="text-xs font-semibold text-red-700">
                                Motivo da não aprovação:{" "}
                            </span>
                            <span className="text-xs text-red-600">
                                {expense.rejectionReason}
                            </span>
                        </div>
                    )}

                    {/* Botões Reutilizáveis alinhados à direita */}
                    <div className="w-full flex justify-content-end gap-2 pt-1">
                        {canCancel && (
                            <MobileActionButton
                                label="Anular"
                                icon="pi pi-trash"
                                severity="danger"
                                onClick={() => onCancel(expense)}
                            />
                        )}
                        <MobileActionButton
                            label="Visualizar"
                            icon="pi pi-eye"
                            severity="secondary"
                            onClick={() => onView(expense)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const Expenses = () => {
    const { isLoading, hasPermission } = usePermissionCheck({ requiredFunction: REQUIRE_FUNCTION });
    const navigationState = useNavigationStore();
    const { userEmployee } = useAuthStore();
    const toast = useRef<Toast>(null);
    const [localExpenses, setLocalExpenses] = useState<Expense[]>([]);


    const [showRegisterModal, setShowRegisterModal] = useState<boolean>(false);
    const [expandedExpenseId, setExpandedExpenseId] = useState<string | null>(null);
    const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);

    const employeeIdForQuery = userEmployee?.id;

    const [lazyParams, setLazyParams] = useState({
        page: 1,
        limit: 100,
        sortBy: "createdAt",
        sortOrder: "desc",
        filters: {} as Record<string, any>,
    });

    const { mutateAsync: deleteExpenseMutation } = useDeleteExpenseMutation();
    const { data: expenses, isPending: isExpensePending, isError, isRefetching, refetch } =
        useExpenseByFilter({
            page: lazyParams.page,
            limit: lazyParams.limit,
            sortBy: lazyParams.sortBy,
            sortOrder: lazyParams.sortOrder,
            ...lazyParams.filters,
            employeeId: employeeIdForQuery,
        } as ExpenseInputFilter);

    useEffect(() => {
        if (navigationState.currentRoute !== FUNCTION) {
            navigationState.setCurrentRoute(FUNCTION);
        }
    }, [navigationState]);

    useEffect(() => {
        if (!expenses?.data || isExpensePending) return;
        setLocalExpenses(expenses.data);
    }, [expenses?.data, isExpensePending]);

    const {
        activeTab,
        setActiveTab,
        filterTabs,
        filteredItems: filteredExpenses,
    } = useStatusFilter(localExpenses, EXPENSE_STATUS_FILTERS, getExpenseStatus);

    const confirmAction = (message: string, acceptCallback: () => void) => {
        confirmDialog({
            message,
            header: "Confirmação",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sim",
            rejectLabel: "Não",
            accept: acceptCallback,
        });
    };

    const handleViewDocument = (expense: Expense) => {
        const doc = expense.document;
        if (doc?.fullPath) {
            setS3PathToDisplay(doc.fullPath);
        }
    };

    const handleRemoveExpense = (expense: Expense) => {
        confirmAction("Deseja anular a submissão desta despesa?", async () => {
            if (!expense.id) return;
            setLocalExpenses((prev) => prev.filter((item) => item.id !== expense.id));
            try {
                await deleteExpenseMutation(expense.id);
                toast.current?.show({
                    severity: "success",
                    summary: "Sucesso",
                    detail: "Despesa anulada com sucesso.",
                    life: 3500,
                });
            } catch (error: unknown) {
                toast.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: getApiErrorMessage(error, "Não foi possível anular a despesa."),
                    life: 3500,
                });
            } finally {
                await refetch();
            }

        });
    };

    const handleToggle = (id: string) => {
        setExpandedExpenseId((prev) => (prev === id ? null : id));
    };

    if (isExpensePending || isLoading) {
        return <ExpenseSkeleton />;
    }

    return (
        <div className="flex flex-column pt-2 px-3 gap-2 pb-8 align-items-center">
            {/* Abas de Filtro Reutilizáveis */}
            <MobileFilterTabs
                tabs={filterTabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                scrollable
            />

            {/* Cabeçalho da Secção (Sem CAPSLOCK) */}
            <div className="w-full flex justify-content-between align-items-center pt-1 px-1">
                <span
                    className="font-semibold text-xs"
                    style={{ color: "#64748B" }}
                >
                    As minhas despesas
                </span>
                {isRefetching && <MobileRefreshingIndicator/>}
            </div>

            {/* Lista de Cartões com Expansão Integrada */}
            <MobileRefreshingList
            isRefreshing={isRefetching}
            className="w-full flex flex-column gap-3">
                {isError ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-exclamation-circle text-4xl text-red-500" />
                        <span className="text-sm text-center text-600">
                            Não foi possível carregar as despesas.
                        </span>
                    </div>
                ) : filteredExpenses.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-euro text-4xl" style={{ color: "#c5c5c5" }} />
                        <span className="text-sm text-center" style={{ color: "#888" }}>
                            {activeTab !== "all"
                                ? "Nenhuma despesa encontrada com este filtro."
                                : "Não tem despesas registadas."}
                        </span>
                    </div>
                ) : (
                    filteredExpenses.map((expense) => (
                        <ExpenseItem
                            key={expense.id}
                            expense={expense}
                            isExpanded={expandedExpenseId === expense.id}
                            onToggle={() => expense.id && handleToggle(expense.id)}
                            onView={handleViewDocument}
                            onCancel={handleRemoveExpense}
                        />
                    ))
                )}
            </MobileRefreshingList>

            {/* Botão Flutuante (+) */}
            <Button
                icon="pi pi-plus"
                rounded
                className="fixed"
                onClick={() => setShowRegisterModal(true)}
                style={{
                    bottom: "6rem",
                    right: "20px",
                    zIndex: 1000,
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#0084D1",
                    border: "none",
                }}
            />

            <ConfirmDialog />

            {/* Visualizadores de Documentos */}
            {s3PathToDisplay &&
                (s3PathToDisplay.toLowerCase().endsWith(".pdf") ? (
                    <MobileS3PDFViewerDialog
                        s3Path={s3PathToDisplay}
                        onHide={() => setS3PathToDisplay(null)}
                    />
                ) : (
                    <MobileImageViewerDialog
                        s3Path={s3PathToDisplay}
                        onHide={() => setS3PathToDisplay(null)}
                    />
                ))}

            <MobileToast ref={toast} />

            <RegisterExpensesModal
                visible={showRegisterModal}
                setVisible={setShowRegisterModal}
                refetch={refetch}
                toastRef={toast}
            />
        </div>
    );
};

export default Expenses;